/*
 * Newsletter popup — a centered subscribe dialog whose content is entirely
 * merchant-authored blocks. Nothing is fetched; the component only decides
 * *when* the dialog opens and remembers that the visitor dismissed it.
 *
 * The modal mechanic (overlay, focus trap, escape, delayed unmount, scroll
 * lock) mirrors quick-view.js; the storage suppression mirrors the inline
 * close script in sections/announcementbar.liquid, including its rule that the
 * theme editor is never suppressed — a merchant has to be able to see the
 * popup while editing it.
 */

/*
 * Kept in sync with --popup-newsletter-duration in popup-newsletter.css.
 *
 * The name is prefixed because theme assets are plain scripts sharing one
 * global scope: popup-notice-sales.js also renders on every page from
 * layout/theme.liquid, and a second top-level `const TRANSITION_DURATION`
 * makes whichever script runs last die with a SyntaxError before it can call
 * customElements.define.
 */
const POPUP_NEWSLETTER_TRANSITION_DURATION = 300;
const DESKTOP_QUERY = '(min-width: 768px)';
const STORAGE_PREFIX = 'amerce_popup_newsletter_';

/*
 * How long a dismissal suppresses the automatic open, in milliseconds.
 * `every_visit` never suppresses and `session` is handled with sessionStorage
 * instead, since a session has no fixed length to compare a timestamp against.
 */
const REPEAT_WINDOWS = {
  daily: 24 * 60 * 60 * 1000,
  weekly: 7 * 24 * 60 * 60 * 1000,
  once: Infinity,
};

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

class PopupNewsletter extends HTMLElement {
  constructor() {
    super();

    this.isOpen = false;
    this.lastFocused = null;

    this.onClick = this.onClick.bind(this);
    this.onDocumentKeydown = this.onDocumentKeydown.bind(this);
    this.onMouseOut = this.onMouseOut.bind(this);
  }

  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';

    this.modal = this.querySelector('[data-popup-newsletter-modal]');
    this.dialog = this.querySelector('[data-popup-newsletter-dialog]');
    this.teaser = this.querySelector('[data-popup-newsletter-open]');

    if (!this.modal || !this.dialog) return;

    this.trigger = this.dataset.trigger || 'delay_5';
    this.repeat = this.dataset.repeat || 'daily';
    this.showTeaser = this.dataset.showTeaser === 'true';
    this.storageKey = `${STORAGE_PREFIX}${this.dataset.sectionId || 'default'}`;
    this.designMode = Boolean(window.Shopify && window.Shopify.designMode);

    /*
     * The page gate is checked before anything is wired up, so a page the
     * merchant excluded pays for no listeners, no timers and no teaser.
     */
    if (!this.isAllowedOnThisPage()) return;

    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.addEventListener('click', this.onClick, { signal });
    document.addEventListener('keydown', this.onDocumentKeydown, { signal });

    this.schedule();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.clearTimers();

    /*
     * The theme editor swaps the whole section node out. If it did so while the
     * dialog was open the scroll lock would otherwise be left on the document.
     */
    if (this.isOpen) this.unlockScroll();
  }

  /*
   * Called when the theme editor reloads this section. Re-running schedule() is
   * idempotent: every timer is cleared and the dialog is reset first.
   */
  restart() {
    this.clearTimers();

    this.isOpen = false;
    this.classList.remove('is-open');
    this.unlockScroll();

    if (this.modal) this.modal.hidden = true;
    if (this.teaser) this.teaser.hidden = true;

    this.schedule();
  }

  schedule() {
    /* Unset when connectedCallback bailed out before wiring anything up. */
    if (!this.abortController) return;
    if (!this.isAllowedOnThisPage()) return;

    /*
     * The visitor has just submitted the form and been redirected back, so the
     * confirmation has to be visible whatever the trigger and repeat settings
     * say.
     */
    if (this.hasPostedSuccessfully()) {
      this.open();
      return;
    }

    /*
     * The theme editor never auto-opens the popup on a timer/exit-intent —
     * that would fight the merchant while they're editing unrelated
     * sections. It still opens on demand: the teaser button, or selecting
     * this section/block (see the shopify:section:select listener below).
     */
    if (this.designMode) {
      this.revealTeaser();
      return;
    }

    if (this.isSuppressed()) {
      /* No automatic open this load, but reopening by hand stays available. */
      this.revealTeaser();
      return;
    }

    const { signal } = this.abortController;

    switch (this.trigger) {
      case 'immediate':
        this.open();
        break;

      case 'delay_10':
        this.openTimeout = setTimeout(() => this.open(), 10000);
        break;

      case 'exit_intent':
        /*
         * There is no pointer to leave the viewport on a touch device, so the
         * documented mobile behaviour is to fall back to opening on load.
         */
        if (window.matchMedia(DESKTOP_QUERY).matches) {
          document.addEventListener('mouseout', this.onMouseOut, { signal });
        } else {
          this.openOnPageLoad();
        }
        break;

      default:
        this.openTimeout = setTimeout(() => this.open(), 5000);
    }
  }

  openOnPageLoad() {
    if (document.readyState === 'complete') {
      this.open();
      return;
    }

    window.addEventListener('load', () => this.open(), {
      once: true,
      signal: this.abortController.signal,
    });
  }

  /* Classic exit-intent heuristic: the pointer leaves through the top edge. */
  onMouseOut(event) {
    if (event.clientY > 0) return;
    if (event.relatedTarget) return;

    this.open();
  }

  onClick(event) {
    if (!(event.target instanceof Element)) return;

    if (event.target.closest('[data-popup-newsletter-close]')) {
      event.preventDefault();
      this.close();
      return;
    }

    if (event.target.closest('[data-popup-newsletter-open]')) {
      event.preventDefault();
      /* An explicit reopen — the repeat suppression does not apply. */
      this.open();
    }
  }

  onDocumentKeydown(event) {
    if (!this.isOpen) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }

    if (event.key === 'Tab') this.trapFocus(event);
  }

  open() {
    if (this.isOpen) return;
    /* The element bails out of connectedCallback when the page gate fails. */
    if (!this.modal || !this.dialog) return;

    this.clearTimers();
    this.lastFocused = document.activeElement;

    this.modal.hidden = false;
    if (this.teaser) {
      this.teaser.hidden = true;
      this.teaser.setAttribute('aria-expanded', 'true');
    }

    this.lockScroll();

    requestAnimationFrame(() => {
      this.classList.add('is-open');
      this.isOpen = true;

      const success = this.dialog.querySelector('[data-newsletter-success-message="popup"]');
      const target = success || this.dialog.querySelector('[data-popup-newsletter-close]');
      target?.focus({ preventScroll: true });
    });
  }

  close(restoreFocus = true) {
    this.classList.remove('is-open');
    this.isOpen = false;

    this.unlockScroll();
    this.teaser?.setAttribute('aria-expanded', 'false');
    this.rememberDismissal();

    this.unmountTimeout = setTimeout(() => {
      if (this.isOpen) return;
      this.modal.hidden = true;
      this.revealTeaser();
    }, POPUP_NEWSLETTER_TRANSITION_DURATION);

    if (restoreFocus && this.lastFocused instanceof HTMLElement) {
      this.lastFocused.focus({ preventScroll: true });
    }
  }

  /*
   * Closed because the merchant selected something else in the theme editor,
   * not because a visitor dismissed it: focus is left where the editor put it,
   * and no dismissal is written (rememberDismissal already no-ops in design
   * mode, so the storefront's repeat window is untouched by editing).
   */
  closeFromEditor() {
    if (!this.isOpen) return;
    this.close(false);
  }

  revealTeaser() {
    if (!this.showTeaser || !this.teaser) return;
    this.teaser.hidden = false;
  }

  lockScroll() {
    document.documentElement.classList.add('overflow-hidden');
    document.body.classList.add('overflow-hidden');
  }

  unlockScroll() {
    document.documentElement.classList.remove('overflow-hidden');
    document.body.classList.remove('overflow-hidden');
  }

  isAllowedOnThisPage() {
    if (this.dataset.showHomeOnly !== 'true') return true;
    return this.dataset.isHome === 'true';
  }

  /*
   * Both this form and the footer newsletter form post as `customer`, so Liquid
   * renders a success message in both after either one succeeds. The
   * `newsletter_form=popup` marker written into return_to is what tells them
   * apart.
   */
  hasPostedSuccessfully() {
    if (!this.querySelector('[data-newsletter-success-message="popup"]')) return false;

    try {
      return new URLSearchParams(window.location.search).get('newsletter_form') === 'popup';
    } catch (error) {
      return false;
    }
  }

  isSuppressed() {
    if (this.designMode) return false;
    if (this.repeat === 'every_visit') return false;

    try {
      if (this.repeat === 'session') {
        return sessionStorage.getItem(this.storageKey) === 'true';
      }

      const dismissedAt = Number(localStorage.getItem(this.storageKey));
      if (!dismissedAt) return false;

      const suppressFor = REPEAT_WINDOWS[this.repeat];
      if (suppressFor === undefined) return false;
      if (suppressFor === Infinity) return true;

      return Date.now() - dismissedAt < suppressFor;
    } catch (error) {
      /* Storage is unavailable (private browsing, blocked cookies). Show it. */
      return false;
    }
  }

  rememberDismissal() {
    if (this.designMode) return;
    if (this.repeat === 'every_visit') return;

    try {
      if (this.repeat === 'session') {
        sessionStorage.setItem(this.storageKey, 'true');
      } else {
        localStorage.setItem(this.storageKey, String(Date.now()));
      }
    } catch (error) {
      /* Nothing to recover from — the popup simply shows again next load. */
    }
  }

  trapFocus(event) {
    const focusable = Array.from(this.dialog.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
      (element) => !element.hidden && element.offsetParent !== null,
    );

    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  clearTimers() {
    clearTimeout(this.openTimeout);
    clearTimeout(this.unmountTimeout);
  }
}

if (!customElements.get('popup-newsletter')) {
  customElements.define('popup-newsletter', PopupNewsletter);
}

document.addEventListener('shopify:section:load', (event) => {
  event.target.querySelector?.('popup-newsletter')?.restart?.();
});

/*
 * Resolve the popup that an editor event belongs to: `closest` covers a block
 * inside the dialog, `querySelector` covers the section wrapper itself. Returns
 * null for any other section, which is what tells the handlers below to close.
 */
function editorEventPopup(target) {
  if (!(target instanceof Element)) return null;
  return target.closest('popup-newsletter') || target.querySelector('popup-newsletter');
}

/*
 * Selecting the section or one of its blocks in the theme editor opens the
 * dialog, so the merchant can see what they are editing — and selecting
 * anything else closes it again. Without the closing half, an editor-opened
 * dialog outlives its selection and covers the section the merchant moved on
 * to, since it is fixed and overlaid on the whole preview.
 */
function onEditorSelect(event) {
  const selected = editorEventPopup(event.target);

  document.querySelectorAll('popup-newsletter').forEach((popup) => {
    if (popup === selected) {
      popup.open?.();
    } else {
      popup.closeFromEditor?.();
    }
  });
}

document.addEventListener('shopify:section:select', onEditorSelect);
document.addEventListener('shopify:block:select', onEditorSelect);

/*
 * Deselecting the popup section itself — closing the section panel, or clicking
 * away from every section — closes the dialog too. Block-level deselects are
 * deliberately ignored: moving between this popup's own blocks deselects one
 * and selects the next, so closing in between would only flicker.
 */
document.addEventListener('shopify:section:deselect', (event) => {
  editorEventPopup(event.target)?.closeFromEditor?.();
});
